import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_B2B_BIZ_USER, IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { overquota256TariffId, preQuotaOfferLimit } from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { sendDwhBanner } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { updateProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getProfileLoadingState, getUserOverquotaCycleCount } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';
import { PromoBlockData } from 'reactApp/ui/Mobile/PromoBlock/PromoBlock.data';
import { ON_PREMISE } from 'server/constants/environment';

const name = 'mnz_avoid_overquota';

export const usePreOverquoterBlock = () => {
    const dispatch = useDispatch();
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const { used: usedPercent } = useSelector(UserQuotaSelectors.getFilledPercents);
    const status = useSelector(getProfileLoadingState);
    const count = useSelector(getUserOverquotaCycleCount);
    const productId = overquota256TariffId;
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));

    const showPreOverquoter =
        !IS_B2B_BIZ_USER &&
        !ON_PREMISE &&
        status?.isLoaded &&
        count > 0 &&
        !isPaidUser &&
        !isOverquota &&
        usedPercent >= preQuotaOfferLimit;

    useEffect(() => {
        if (!productId || product || !showPreOverquoter) {
            return;
        }

        dispatch(updateProducts({ productId }));
    }, [productId, product, showPreOverquoter]);

    const onShow = useCallback(() => {
        sendDwhBanner({ action: 'view', name });
    }, []);

    const onClick = useCallback(() => {
        sendDwhBanner({ action: 'click-tariff', name });

        dispatch(
            openTariffBuy({
                productId,
                isMobile: IS_PHONE_BROWSER,
                paySource: getPaySource(`avoid_overquota_banner`),
            })
        );
    }, [productId]);

    const onSecondaryClick = useCallback(() => {
        sendDwhBanner({ action: 'click', name, button_name: 'other_tariffs' });

        dispatch(
            historyPush({
                id: '/promo/quota',
                search: `?${PaymentUTM.preOverquota}`,
                hash: 'tariffs',
            })
        );
    }, []);

    const preOverquotaPromoData = { onShow, onClick, onSecondaryClick, doNotClose: true };

    return {
        showPreOverquoter,
        preOverquotaPromoProps: PromoBlockData.PRE_OVERQUOTA,
        preOverquotaPromoData,
    };
};
